import { z } from "zod";
import { optional, storableText } from "./appointments";
import { recordId } from "./contracts";
import { absentOrBlankAsNull, searchTextUpTo, unset } from "./fields";
import { emailAddress, SETTINGS_DESCRIPTION_MAX_LENGTH, SETTINGS_SHORT_TEXT_MAX_LENGTH, settingsName } from "./organization-settings";

export const ADMIN_ORGANIZATION_PAGE_SIZE_MAX = 100;

const SEARCH_MAX_LENGTH = 200;

const count = z.number().int().nonnegative();

export const adminOrganizationListQuery = z.object({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(ADMIN_ORGANIZATION_PAGE_SIZE_MAX).default(20)),
  search: optional(searchTextUpTo(SEARCH_MAX_LENGTH)),
});
export type AdminOrganizationListQuery = z.infer<typeof adminOrganizationListQuery>;

export const adminOrganizationStatus = z.enum(["active", "inactive"]);
export type AdminOrganizationStatus = z.infer<typeof adminOrganizationStatus>;

export const adminCustomer = z.object({
  id: recordId,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  company: z.string(),
  createdAt: z.string(),
  status: adminOrganizationStatus,
  description: z.string().optional(),
});
export type AdminCustomer = z.infer<typeof adminCustomer>;

export const adminVendor = z.object({
  id: recordId,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  category: z.string(),
  createdAt: z.string(),
  status: adminOrganizationStatus,
  description: z.string().optional(),
  image: z.string().optional(),
});
export type AdminVendor = z.infer<typeof adminVendor>;

export const adminSubcontractor = z.object({
  id: recordId,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  trade: z.string(),
  licenseNumber: z.string(),
  createdAt: z.string(),
  status: adminOrganizationStatus,
});
export type AdminSubcontractor = z.infer<typeof adminSubcontractor>;

const pageOf = <T extends z.ZodTypeAny>(item: T) => z.object({ data: z.array(item), total: count, page: count, pageSize: count, totalPages: count });

export const adminCustomerPage = pageOf(adminCustomer);
export type AdminCustomerPage = z.infer<typeof adminCustomerPage>;

export const adminVendorPage = pageOf(adminVendor);
export type AdminVendorPage = z.infer<typeof adminVendorPage>;

export const adminSubcontractorPage = pageOf(adminSubcontractor);
export type AdminSubcontractorPage = z.infer<typeof adminSubcontractorPage>;

const optionalText = (maxLength: number) => z.preprocess(absentOrBlankAsNull, storableText(maxLength).nullable());

export const adminOrganizationDetailsRequest = z
  .object({
    displayName: settingsName,
    email: emailAddress,
    description: optionalText(SETTINGS_DESCRIPTION_MAX_LENGTH),
    phone: optionalText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
  })
  .strict();
export type AdminOrganizationDetailsRequest = { displayName: string; email: string; description?: string | null; phone?: string | null };

export const adminSubcontractorCreateRequest = z
  .object({
    vendor: recordId,
    name: settingsName,
    email: emailAddress,
    description: optionalText(SETTINGS_DESCRIPTION_MAX_LENGTH),
    phone: optionalText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
  })
  .strict();
export type AdminSubcontractorCreateRequest = { vendor: string; name: string; email: string; description?: string | null; phone?: string | null };

export const adminSubcontractorUpdateRequest = z
  .object({
    name: settingsName,
    email: emailAddress,
    phone: optionalText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
    trade: optionalText(SETTINGS_DESCRIPTION_MAX_LENGTH),
    licenseNumber: optionalText(SETTINGS_SHORT_TEXT_MAX_LENGTH),
    status: optional(adminOrganizationStatus),
  })
  .strict();
export type AdminSubcontractorUpdateRequest = {
  name: string;
  email: string;
  phone?: string | null;
  trade?: string | null;
  licenseNumber?: string | null;
  status?: AdminOrganizationStatus;
};

export const adminOrganizationLogo = z.object({ id: recordId, logo: z.string() });
export type AdminOrganizationLogo = z.infer<typeof adminOrganizationLogo>;

export const adminShadowSession = z.object({ actionLink: z.string().url(), targetEmail: z.string() });
export type AdminShadowSession = z.infer<typeof adminShadowSession>;
